import type { ImportedModelOccurrenceIndex } from '../occurrence'
import { ImportedModelInstancedMeshKeyFactory } from './ImportedModelInstancedMeshKeyFactory'

/**
 * 从 occurrence 索引构建只读实例化渲染槽。
 *
 * Builder 只组织共享定义、Geometry、Material 和局部 slot 结构，不创建任何 GPU 资源，
 * 因而可以在正式发布前完成完整资格分析和失败降级。
 */
export class ImportedModelRenderPrototypeBuilder {
  /** 集中生成实例组等价键的工厂。 */
  private readonly keyFactory = new ImportedModelInstancedMeshKeyFactory()

  /**
   * 构建全部潜在实例化槽。
   *
   * @param occurrenceIndex 当前导入结果的 occurrence 索引。
   * @returns 按实例组键确定排序的只读渲染槽。
   */
  public build(
    occurrenceIndex: ImportedModelOccurrenceIndex,
  ): readonly PuppyCAD.ImportedModelInstancingRenderSlot[] {
    const slotMembersByKey = new Map<string, PuppyCAD.ImportedModelInstancingSlotMember[]>()
    const slotSeedByKey = new Map<string, PuppyCAD.ImportedModelInstancingSlotMember>()
    const definitionKeyByUnitKey = new Map<string, string>()

    for (const definitionKey of occurrenceIndex.listDefinitionKeys()) {
      const occurrences = occurrenceIndex.listOccurrencesByDefinition(definitionKey)
      if (occurrences.length < 2) {
        continue
      }
      for (const occurrence of occurrences) {
        for (const sourceMesh of occurrence.sourceMeshes) {
          if (!this.isEffectivelyVisible(sourceMesh)) {
            continue
          }
          const unitKey = this.keyFactory.create(
            definitionKey,
            occurrence.rootObject,
            sourceMesh,
          )
          const member = Object.freeze({
            occurrenceId: occurrence.occurrenceId,
            occurrenceRoot: occurrence.rootObject,
            sourceMesh,
          })
          const members = slotMembersByKey.get(unitKey) ?? []
          members.push(member)
          slotMembersByKey.set(unitKey, members)
          slotSeedByKey.set(unitKey, slotSeedByKey.get(unitKey) ?? member)
          definitionKeyByUnitKey.set(unitKey, definitionKey)
        }
      }
    }

    return Object.freeze([...slotMembersByKey.entries()]
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([unitKey, members]) => {
        const seed = slotSeedByKey.get(unitKey)
        if (!seed) {
          throw new Error(`实例化渲染槽缺少种子 Mesh：${unitKey}`)
        }
        const definitionKey = definitionKeyByUnitKey.get(unitKey)
        if (!definitionKey) {
          throw new Error(`实例化渲染槽缺少 definition 身份：${unitKey}`)
        }
        return Object.freeze({
          definitionKey,
          slotKey: unitKey,
          unitKey,
          geometry: seed.sourceMesh.geometry,
          material: seed.sourceMesh.material,
          members: Object.freeze([...members].sort((left, right) => (
            left.occurrenceId.localeCompare(right.occurrenceId)
          ))),
        })
      }))
  }

  /**
   * 判断 source Mesh 在接管前是否沿父链保持业务可见。
   *
   * @param sourceMesh 待判断原始显示 Mesh。
   * @returns 当前 occurrence 是否应进入正式实例槽。
   */
  private isEffectivelyVisible(sourceMesh: import('three').Mesh): boolean {
    let current: import('three').Object3D | null = sourceMesh
    while (current) {
      if (!current.visible) {
        return false
      }
      current = current.parent
    }
    return true
  }
}
